import { Box, CircularProgress, Stack } from "@mui/material";

import { Text } from "../../../components/Text";
import { ProfessionalListItem } from "./ProfessionalListItem";
import { type InviteFlowStatus, type Professional } from "./types";

interface ProfessionalListProps {
  allProfessionals: Professional[];
  starredProfessionals: Professional[];
  recentProfessionals: Professional[];
  selectedProfessional: Professional | undefined;
  isSearching: boolean;
  searchQuery: string;
  status: InviteFlowStatus;
  onSelectProfessional: (professional: Professional) => void;
}

/**
 * Renders the list of professionals with sections for starred and other professionals.
 */
export function ProfessionalList(props: ProfessionalListProps) {
  const {
    allProfessionals,
    starredProfessionals,
    recentProfessionals,
    selectedProfessional,
    isSearching,
    searchQuery,
    status,
    onSelectProfessional,
  } = props;

  const hasStarredProfessionals = starredProfessionals.length > 0;
  const hasRecentProfessionals = recentProfessionals.length > 0;
  const isActiveSearch = searchQuery.length > 0;

  const isSelectedInAnyList =
    selectedProfessional !== undefined &&
    allProfessionals.some((p) => p.id === selectedProfessional.id);
  const shouldShowSelectedSection =
    selectedProfessional !== undefined && !isSelectedInAnyList && !isActiveSearch;

  if (isSearching && allProfessionals.length === 0 && !shouldShowSelectedSection) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 8,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (allProfessionals.length === 0 && !shouldShowSelectedSection) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          padding: 4,
        }}
      >
        <Text
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            textAlign: "center",
          })}
          variant="body2"
        >
          {searchQuery
            ? "No professionals found matching your search."
            : "Search for a professional to invite."}
        </Text>
      </Box>
    );
  }

  const recentIds = new Set(recentProfessionals.map((p) => p.id));
  const otherProfessionals = allProfessionals.filter((p) => !p.isStarred && !recentIds.has(p.id));
  const hasOtherProfessionals = otherProfessionals.length > 0;
  const isSending = status === "sending";

  return (
    <Stack spacing={0}>
      {shouldShowSelectedSection && selectedProfessional && (
        <>
          <SectionHeader title="Selected Professional" />
          <ProfessionalListItem
            key={selectedProfessional.id}
            isSelected
            disabled={isSending}
            professional={selectedProfessional}
            onSelect={onSelectProfessional}
          />
        </>
      )}

      {isActiveSearch && (
        <SectionHeader
          title={`Found ${allProfessionals.length} professional${
            allProfessionals.length === 1 ? "" : "s"
          }`}
        />
      )}

      {hasStarredProfessionals && (
        <>
          {!isActiveSearch && <SectionHeader title="Starred Professionals" />}
          {starredProfessionals.map((professional) => (
            <ProfessionalListItem
              key={professional.id}
              disabled={isSending}
              isSelected={selectedProfessional?.id === professional.id}
              professional={professional}
              onSelect={onSelectProfessional}
            />
          ))}
        </>
      )}

      {hasRecentProfessionals && (
        <>
          {!isActiveSearch && <SectionHeader title="Recent Professionals" />}
          {recentProfessionals.map((professional) => (
            <ProfessionalListItem
              key={professional.id}
              disabled={isSending}
              isSelected={selectedProfessional?.id === professional.id}
              professional={professional}
              onSelect={onSelectProfessional}
            />
          ))}
        </>
      )}

      {hasOtherProfessionals && (
        <>
          {!isActiveSearch && (hasStarredProfessionals || hasRecentProfessionals) && (
            <SectionHeader title="Other Professionals" />
          )}
          {otherProfessionals.map((professional) => (
            <ProfessionalListItem
              key={professional.id}
              disabled={isSending}
              isSelected={selectedProfessional?.id === professional.id}
              professional={professional}
              onSelect={onSelectProfessional}
            />
          ))}
        </>
      )}
    </Stack>
  );
}

interface SectionHeaderProps {
  title: string;
}

function SectionHeader(props: SectionHeaderProps) {
  const { title } = props;

  return (
    <Box
      sx={(theme) => ({
        paddingX: 4,
        paddingY: 2,
        backgroundColor: theme.palette.background.default,
      })}
    >
      <Text
        semibold
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        })}
        variant="caption"
      >
        {title}
      </Text>
    </Box>
  );
}
